import React, { useEffect, useMemo } from 'react'
import {
  Formik,
  Form,
  FormikProps,
  setNestedObjectValues,
  FormikTouched,
} from 'formik'
import { Button, Heading, Hr, Icon, Paragraph } from '@pandiafi/design-system'
import { ContextNotification } from '@pandiafi/components'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'

import initialValues, { FormValues } from './initialValues'
import { TerminateContractPreview } from './Preview'
import {
  Beneficiary,
  Contract,
  ContractTenant,
  FeatureFlag,
  terminateContractInputSchema,
  UserMetadata,
} from '../../../../common/validations'
import { ContractStatus, ResidentAppErrorCode } from '../../../../common/types'
import { ContractToTenantRole } from '../../../../common/types'
import { TerminateContractSuccess } from './Success'
import { TerminateContractViewState } from '..'
import { ResidentAppError } from '../../../../common/error'
import { TerminationDisallowedBanner } from '../components/TerminationDisallowedBanner/TerminationDisallowedBanner'
import { TenantSelector } from '../components/TenantSelector/TenantSelector'
import { TerminateInstructions } from '../components/TerminateInstructions/TerminateInstructions'
import { MovingOutDateField } from '../components/MovingOutDateField/MovingOutDateField'
import { EndDateField } from '../components/EndDateField/EndDateField'
import { TerminationReasonField } from '../components/TerminationReasonField/TerminationReasonField'
import { MovingDestinationField } from '../components/MovingDestinationField/MovingDestinationField'
import { FilesField } from '../components/FilesField/FilesField'
import { PaymentHeader } from '../components/PaymentHeader/PaymentHeader'
import { Beneficiaries } from '../components/Beneficiaries/Beneficiaries'
import { DisplaysSelfField } from '../components/DisplaysSelfField/DisplaysSelfField'
import { PresenterTenantField } from '../components/PresenterTenantField/PresenterTenantField'
import { AdditionalDetailsField } from '../components/AdditionalDetailsField/AdditionalDetailsField'
import { GivingContactInfoAllowedField } from '../components/GivingContactInfoAllowedField/GivingContactInfoAllowedField'
import { HasPetsInApartmentField } from '../components/HasPetsInApartmentField/HasPetsInApartmentField'
import { TerminationNavigationGuard } from '../components/TerminationNavigationGuard/TerminationNavigationGuard'
import {
  newBeneficiary,
  calculateRemainingPercentage,
  calculateRemainingRooReturnPercentage,
} from './initialBeneficiaries'
import { PostalAddressFields } from '../components/PostalAddressFields/PostalAddressFields'
import { formatFormValues } from '../utils/formatFormValues'
import { validateFormValues } from '../../../utils'
import {
  getAllowedMonthEndDates,
  isAllowedMonthEndDateWithinRange,
} from '../../../utils/terminationDateUtils'
import { getTenantName } from '../../../utils/getTenantName'
import { resolveContractTerminationTerminology } from '../utils/resolveContractTerminationTerminology'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { Breakpoint } from 'components'
import { Error } from 'components'
import { FORM_FIELD_VALIDATION_ERROR_CLASSNAME } from 'components/FormField/FormFieldConstants'

interface TerminateContractFormProps {
  view: TerminateContractViewState
  setView: (view: TerminateContractViewState) => void
  terminateTenants: number[]
  setTerminateTenants: (tenantIds: number[]) => void
  tenantSelectionConfirmed: boolean
  setTenantSelectionConfirmed: (value: boolean) => void
  tenants: ContractTenant[]
  beneficiaries: Beneficiary[]
  contract: Contract
  userMetadata: UserMetadata
  featureFlags?: FeatureFlag
}

export const TerminateContractForm: React.FC<TerminateContractFormProps> = ({
  contract,
  view,
  setView,
  terminateTenants,
  setTerminateTenants,
  tenantSelectionConfirmed,
  setTenantSelectionConfirmed,
  tenants,
  beneficiaries,
  userMetadata,
  featureFlags,
}) => {
  const [params] = useSearchParams()
  const { t } = useTranslation(['errors', 'common', 'terminateContract'])
  const [isUploading, setIsUploading] = React.useState(false)
  const [isConfirmingTermination, setIsConfirmingTermination] =
    React.useState(false)
  const terminology = resolveContractTerminationTerminology(
    contract.contractType
  )

  const moduleConfig = useModuleConfig()
  const showShowingSection =
    moduleConfig.terminateContract?.showingVisible ?? true
  const showingDefault =
    moduleConfig.terminateContract?.showingDefault ?? 'tenant'
  const endDateLastDayOfMonth =
    moduleConfig.terminateContract?.endDateLastDayOfMonth ?? true

  const terminationDisallowed = contract.terminationDisallowedReason
  const isFixedTerm =
    contract.status === ContractStatus.ACTIVE && contract.endDate

  const presenterTenantOptions = useMemo(
    () =>
      tenants
        .filter(
          (tenant) =>
            'isAdult' in tenant &&
            !tenant.nonDisclosure &&
            (tenant.tenantRole === ContractToTenantRole.TENANT ||
              tenant.isContractParty ||
              tenant.isPayer)
        )
        .map((tenant) => ({
          id: tenant.tenantUuid,
          label: getTenantName(tenant),
        }))
        .filter((option) => !!option.id),
    [tenants]
  )

  useEffect(() => {
    // Show success view if the contract was successfully terminated
    if (params.get('success')) {
      setView('success')
    }

    // Throw backend errors
    const error = params.get('error')

    if (error) {
      // The error is handled in the ErrorHandler component
      throw ResidentAppError.ClientError(error as ResidentAppErrorCode)
    }
  }, [params, setView])

  const handleValidate = (formValues: FormValues) => {
    const allValidationErrors = validateFormValues(
      formatFormValues(formValues, false, beneficiaries),
      terminateContractInputSchema
    )

    const selectedDate = DateTime.fromISO(formValues.endDate)

    if (
      showShowingSection &&
      formValues.displaysSelf &&
      !formValues.presenterTenantUuid
    ) {
      allValidationErrors.presenterTenantUuid = t('errors:validation.required')
    }

    if (
      !allValidationErrors.endDate &&
      selectedDate.isValid &&
      selectedDate.startOf('day') <
        formValues.firstPossibleEndDate.startOf('day')
    ) {
      allValidationErrors.endDate = t('errors:validation.tooSmallDate')
    }

    if (endDateLastDayOfMonth) {
      const allowedDates = getAllowedMonthEndDates(
        formValues.firstPossibleEndDate,
        formValues.lastPossibleEndDate
      )

      if (
        !allValidationErrors.endDate &&
        !isAllowedMonthEndDateWithinRange(
          formValues.endDate,
          formValues.firstPossibleEndDate,
          formValues.lastPossibleEndDate,
          allowedDates
        )
      ) {
        allValidationErrors.endDate = t(
          'common:datePicker.monthEndOnlyValidation'
        )
      }
    }

    return allValidationErrors
  }

  const handlePreview = async (form: FormikProps<FormValues>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      // Mark fields with errors as touched to show error messages
      await form.setTouched(
        setNestedObjectValues<FormikTouched<FormValues>>(errors, true)
      )
      document
        .querySelector(`.${FORM_FIELD_VALIDATION_ERROR_CLASSNAME}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
      setView('preview')
    }
  }

  const handleTerminateTenantsChange = (tenantIds: number[]) => {
    setTerminateTenants(tenantIds)
    setTenantSelectionConfirmed(false)
  }

  const cancelForm = (form: FormikProps<FormValues>) => {
    window.scrollTo(0, 0)
    form.resetForm()
    setTerminateTenants([])
    setTenantSelectionConfirmed(false)
  }

  if (
    contract.startDate &&
    DateTime.now() < DateTime.fromISO(contract.startDate)
  ) {
    return (
      <Error
        title={t('terminateContract:errors.tooEarlyToTerminate')}
        message={t('terminateContract:errors.tooEarlyToTerminateMessage')}
      />
    )
  }

  const initialFormValues = initialValues(
    contract,
    tenants,
    beneficiaries,
    t('terminateContract:texts.propertyCompanyName'),
    t('terminateContract:texts.propertyCompanyAddress'),
    DateTime.now(),
    showingDefault,
    endDateLastDayOfMonth
  )

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={() => {}}
      validate={handleValidate}
    >
      {(form: FormikProps<FormValues>) => {
        const shouldWarnBeforeLeaving =
          (view === 'form' && (form.dirty || terminateTenants.length > 0)) ||
          (view === 'preview' && !isConfirmingTermination)

        const allowedMonthEndDates = endDateLastDayOfMonth
          ? getAllowedMonthEndDates(
              form.values.firstPossibleEndDate,
              form.values.lastPossibleEndDate
            )
          : []

        const noValidMonthEndDates =
          endDateLastDayOfMonth && allowedMonthEndDates.length === 0

        return (
          <>
            <TerminationNavigationGuard shouldWarn={shouldWarnBeforeLeaving} />
            {view === 'success' && (
              <TerminateContractSuccess contractType={contract.contractType} />
            )}
            {view === 'preview' && (
              <TerminateContractPreview
                userMetadata={userMetadata}
                contract={contract}
                formValues={form.values}
                cancel={() => {
                  setIsConfirmingTermination(false)
                  setView('form')
                }}
                tenants={tenants}
                initialBeneficiaries={beneficiaries}
                setIsConfirming={setIsConfirmingTermination}
              />
            )}
            {view === 'form' && (
              <Form>
                <Breakpoint show tablet desktop>
                  <TerminationDisallowedBanner
                    reason={terminationDisallowed}
                    contractType={contract.contractType}
                  />
                </Breakpoint>

                <Heading element="h3" size="l" className="phone:font-medium">
                  {t('terminateContract:headings.parties')}
                </Heading>

                <TenantSelector
                  tenants={tenants}
                  contract={contract}
                  terminate={terminateTenants}
                  handleTerminateChange={handleTerminateTenantsChange}
                  featureFlags={featureFlags}
                />

                {terminateTenants.length > 0 && !tenantSelectionConfirmed && (
                  <div className="mt-8 flex items-center justify-end gap-4">
                    <Button
                      type="button"
                      variant="cancel"
                      onClick={() => cancelForm(form)}
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={terminationDisallowed !== undefined}
                      onClick={() => setTenantSelectionConfirmed(true)}
                    >
                      {t('common:next')}
                    </Button>
                  </div>
                )}

                {tenantSelectionConfirmed && terminateTenants.length > 0 && (
                  <>
                    <Hr className="my-10 border-[#cbcbcb]" />

                    <Heading size="l" element="h3" className="mb-4 font-medium">
                      {t(terminology.formHeading)}
                    </Heading>

                    {isFixedTerm && (
                      <ContextNotification type="warning">
                        {t('terminateContract:texts.fixedTermAlert')}
                      </ContextNotification>
                    )}

                    {noValidMonthEndDates && (
                      <ContextNotification type="warning" className="mb-4">
                        {t('terminateContract:texts.monthEndDateUnavailable')}
                      </ContextNotification>
                    )}

                    {!isFixedTerm && (
                      <TerminateInstructions
                        contract={contract}
                        formValues={form.values}
                      />
                    )}

                    <div className="flex gap-5 phone:flex-col">
                      <div className="w-full tablet:w-1/2">
                        <MovingOutDateField />
                      </div>
                      <div className="w-full tablet:w-1/2">
                        <EndDateField />
                      </div>
                    </div>

                    <TerminationReasonField
                      contractType={contract.contractType}
                    />
                    <MovingDestinationField />
                    <Paragraph small className="mt-2">
                      {t(
                        'terminateContract:texts.movingDestinationInstructions'
                      )}
                    </Paragraph>

                    <FilesField isUploading={setIsUploading} />

                    <Heading
                      size="l"
                      element="h3"
                      className="mb-6 mt-16 font-medium"
                    >
                      {t('terminateContract:headings.paymentAndContactInfo')}
                    </Heading>

                    <PaymentHeader contractType={contract.contractType} />

                    <Heading size="l" element="h3" className="mb-6 mt-12">
                      {t('terminateContract:headings.paymentInfo')}
                    </Heading>

                    <Beneficiaries beneficiaries={form.values.beneficiaries} />

                    <Button
                      onClick={() => {
                        const remaining = calculateRemainingPercentage(
                          form.values.beneficiaries
                        )
                        const remainingRooReturnPercentage =
                          calculateRemainingRooReturnPercentage(
                            form.values.beneficiaries
                          )
                        form.setFieldValue('beneficiaries', [
                          ...form.values.beneficiaries,
                          {
                            ...newBeneficiary(),
                            returnPercentage: remaining,
                            rooReturnPercentage: remainingRooReturnPercentage,
                          },
                        ])
                      }}
                      type="button"
                      variant="add"
                      disabled={form.values.beneficiaries.length >= 4}
                      className={classNames(
                        'border-1 border-dashed text-center text-[var(--brand-7)] border-[--var(--common-gray-6)]',
                        'flex justify-center items-center py-6 px-12 text-sm w-full'
                      )}
                    >
                      <Icon
                        icon="plus-circle"
                        className={classNames('text-[var(--brand-3)] mr-2')}
                      />
                      {t('terminateContract:texts.addPaymentInfo')}
                    </Button>

                    <Heading size="l" element="h3" className="mb-4 mt-16">
                      {t('terminateContract:headings.newAddress')}
                    </Heading>
                    {tenants.some((tenant) => tenant.nonDisclosure) && (
                      <ContextNotification type="warning" className="mb-4">
                        {t(
                          'terminateContract:texts.securityRestrictionWarning'
                        )}
                      </ContextNotification>
                    )}

                    <Paragraph large className="mb-8">
                      {t('terminateContract:texts.newAddressReason')}
                    </Paragraph>

                    <PostalAddressFields />

                    {showShowingSection && (
                      <>
                        <Heading
                          size="l"
                          element="h3"
                          className="mb-4 mt-16 font-medium"
                        >
                          {t('terminateContract:headings.showing')}
                        </Heading>

                        <DisplaysSelfField />
                        <PresenterTenantField
                          options={presenterTenantOptions}
                        />
                      </>
                    )}

                    <div className="phone:mt-8 min-tablet:mt-16 flex flex-col items-end space-y-3">
                      <GivingContactInfoAllowedField />
                      {showShowingSection && <HasPetsInApartmentField />}
                    </div>

                    <Heading
                      size="l"
                      element="h3"
                      className="mb-4 mt-16 font-medium"
                    >
                      {t('terminateContract:headings.additionalDetails')}
                    </Heading>
                    <Paragraph large className="mb-6">
                      {t(terminology.additionalDetailsInfo)}
                    </Paragraph>
                    <AdditionalDetailsField
                      contractType={contract.contractType}
                    />

                    <ContextNotification type="info" className="mt-10">
                      {t(terminology.beforeConfirmationInstruction)}
                    </ContextNotification>

                    <div className="mt-6 flex items-center justify-end">
                      <Button
                        type="button"
                        variant="cancel"
                        onClick={() => cancelForm(form)}
                      >
                        {t('common:cancel')}
                      </Button>

                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => handlePreview(form)}
                        disabled={isUploading || noValidMonthEndDates}
                      >
                        {t('terminateContract:buttons.confirmTermination')}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            )}
          </>
        )
      }}
    </Formik>
  )
}
